import React, { useState } from 'react';
import { DynamicPricingResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import DynamicPricingSimulation from '../simulations/DynamicPricingSimulation';
import { TrendingUp, DollarSign, Zap } from 'lucide-react';
import { modelStories } from '../../data/stories';

interface DynamicPricingModuleProps {
  data: DynamicPricingResult | null;
  onOptimizationComplete: (data: DynamicPricingResult) => void;
  onLoadingStart: () => void;
}

const DynamicPricingModule: React.FC<DynamicPricingModuleProps> = ({
  data,
  onOptimizationComplete,
  onLoadingStart,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');
  const [companyId, setCompanyId] = useState('COMP001');
  const [objective, setObjective] = useState('profit');
  const [minMargin, setMinMargin] = useState(0.20);
  const [maxDiscount, setMaxDiscount] = useState(0.30);

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizePricing({
      company_id: companyId,
      optimization_objective: objective,
      min_margin: minMargin,
      max_discount: maxDiscount,
    });
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Dynamic Pricing & Revenue Optimization</h2>
        <p className="text-gray-600 mb-4">
          Optimize product prices using demand elasticity modeling and competitive intelligence
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.dynamicPricing} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <form className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="label">Company ID</label>
                <input
                  type="text"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">Optimization Objective</label>
                <select
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  className="input-field"
                >
                  <option value="revenue">Revenue Maximization</option>
                  <option value="profit">Profit Maximization</option>
                  <option value="market_share">Market Share Growth</option>
                </select>
              </div>
              <div>
                <label className="label">Min Margin (%)</label>
                <input
                  type="number"
                  value={minMargin * 100}
                  onChange={(e) => setMinMargin(Number(e.target.value) / 100)}
                  className="input-field"
                  min="10"
                  max="50"
                  step="5"
                />
              </div>
              <div>
                <label className="label">Max Discount (%)</label>
                <input
                  type="number"
                  value={maxDiscount * 100}
                  onChange={(e) => setMaxDiscount(Number(e.target.value) / 100)}
                  className="input-field"
                  min="0"
                  max="50"
                  step="5"
                />
              </div>
            </form>

            <button type="button" onClick={handleOptimize} className="btn-primary">
              <Zap className="w-5 h-5 mr-2" />
              Optimize Prices
            </button>
          </div>

          {data && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="stat-card">
                  <div className="text-sm text-gray-600 mb-1">Revenue Lift</div>
                  <div className="text-2xl font-bold text-green-600 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-1" />
                    {formatCurrency(data.metrics.revenue_lift)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatPercent(data.metrics.revenue_lift_pct)}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="text-sm text-gray-600 mb-1">Profit Lift</div>
                  <div className="text-2xl font-bold text-blue-600 flex items-center">
                    <DollarSign className="w-5 h-5 mr-1" />
                    {formatCurrency(data.metrics.profit_lift)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatPercent(data.metrics.profit_lift_pct)}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="text-sm text-gray-600 mb-1">Products Optimized</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {data.metrics.num_products}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {data.metrics.num_recommendations} recommendations
                  </div>
                </div>
                <div className="stat-card">
                  <div className="text-sm text-gray-600 mb-1">Avg Price Change</div>
                  <div className="text-2xl font-bold text-orange-600">
                    {formatPercent(data.metrics.avg_price_change_pct)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Execution: {data.solve_time_seconds?.toFixed(1)}s
                  </div>
                </div>
              </div>

              {/* Top Recommendations */}
              <div className="table-container">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Top Price Recommendations
                  </h3>
                  <div className="text-sm text-gray-600">
                    Showing top {Math.min(10, data.recommendations.length)} by revenue impact
                  </div>
                </div>
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Price</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Optimal Price</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Change</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue Lift</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Elasticity</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.recommendations.slice(0, 10).map((rec, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {rec.product_name}
                          <div className="text-xs text-gray-500">{rec.category}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-700">
                          {formatCurrency(rec.current_price)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right font-semibold text-blue-600">
                          {formatCurrency(rec.optimal_price)}
                        </td>
                        <td className={`px-6 py-4 text-sm text-right font-semibold ${
                          rec.price_change_pct > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatPercent(rec.price_change_pct)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">
                          {formatCurrency(rec.revenue_lift)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-700">
                          {rec.margin_pct.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            rec.elasticity < -2.5 ? 'bg-red-100 text-red-800' :
                            rec.elasticity < -1.5 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {rec.elasticity.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Price Distribution */}
              <div className="section-card">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Price Change Distribution</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {data.distribution.price_decreases}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Price Decreases</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Avg: {formatPercent(data.distribution.avg_decrease)}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600">
                      {data.distribution.no_change}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">No Change</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Already optimal
                    </div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {data.distribution.price_increases}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Price Increases</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Avg: {formatPercent(data.distribution.avg_increase)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Elasticity Insights */}
              <div className="section-card">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Demand Elasticity Insights</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="text-lg font-bold text-red-600">
                      {data.elasticity_summary.highly_elastic}
                    </div>
                    <div className="text-sm text-gray-600">Highly Elastic</div>
                    <div className="text-xs text-gray-500 mt-1">ε &lt; -2.5</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="text-lg font-bold text-yellow-600">
                      {data.elasticity_summary.elastic}
                    </div>
                    <div className="text-sm text-gray-600">Elastic</div>
                    <div className="text-xs text-gray-500 mt-1">-2.5 &lt; ε &lt; -1.5</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">
                      {data.elasticity_summary.unit_elastic}
                    </div>
                    <div className="text-sm text-gray-600">Unit Elastic</div>
                    <div className="text-xs text-gray-500 mt-1">-1.5 &lt; ε &lt; -0.8</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">
                      {data.elasticity_summary.inelastic}
                    </div>
                    <div className="text-sm text-gray-600">Inelastic</div>
                    <div className="text-xs text-gray-500 mt-1">ε &gt; -0.8</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <div className="section-card">
          <DynamicPricingSimulation />
        </div>
      )}
    </div>
  );
};

export default DynamicPricingModule;
